import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiGet, apiPatch } from '../utils/api';
import { formatCurrency } from '../utils/formatCurrency';
import { useVatMode } from '../hooks/useVatMode';
import { BTN, BTN_LG } from '../constants/buttons';
import { LISTING_STATUS_COLORS } from '../constants/colors';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import { MODAL_OVERLAY } from '../constants/overlayLayers';

function MarketplaceListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { formatMoney } = useVatMode();

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIdx, setSelectedImageIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  const fetchListing = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiGet(`/api/marketplace/listings/${id}`);
      if (!res.ok) throw new Error('Failed to load listing');
      const data = await res.json();
      setListing(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchListing();
  }, [fetchListing]);

  const handleStatusToggle = async () => {
    if (!listing) return;
    const newStatus = listing.status === 'paused' ? 'active' : 'paused';
    try {
      setStatusLoading(true);
      const res = await apiPatch(`/api/marketplace/listings/${id}/status`, { status: newStatus });
      if (!res.ok) throw new Error('Failed to update status');
      setListing(prev => ({ ...prev, status: newStatus }));
    } catch (err) {
      setError(err.message);
    } finally {
      setStatusLoading(false);
    }
  };

  const images = listing?.images || [];
  const hasImages = images.length > 0;

  if (loading) return <LoadingSpinner message="Loading listing..." />;
  if (error) return <ErrorMessage error={error} />;
  if (!listing) return <ErrorMessage error="Listing not found" />;

  return (
    <div className="max-w-4xl mx-auto p-2 md:p-4 lg:p-6">
      {/* Back link */}
      <Link
        to="/hireforge"
        className="inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline mb-4"
      >
        <span>&larr;</span> Back to Hire Forge
      </Link>

      {/* Photo Gallery */}
      <div className="mb-6">
        {hasImages ? (
          <>
            {/* Mobile: horizontal scroll */}
            <div className="md:hidden overflow-x-auto snap-x snap-mandatory flex gap-2 -mx-2 px-2 pb-2">
              {images.map((img, idx) => (
                <img
                  key={idx}
                  src={img.file_path}
                  alt={`${listing.title} ${idx + 1}`}
                  className="min-w-full aspect-[4/3] object-cover rounded-lg snap-center flex-shrink-0"
                  onClick={() => { setSelectedImageIdx(idx); setLightboxOpen(true); }}
                />
              ))}
            </div>

            {/* Desktop: main image + thumbnails */}
            <div className="hidden md:block">
              <img
                src={images[selectedImageIdx]?.file_path}
                alt={listing.title}
                className="w-full aspect-[4/3] object-cover rounded-lg cursor-pointer bg-gray-100 dark:bg-gray-700"
                onClick={() => setLightboxOpen(true)}
              />
              {images.length > 1 && (
                <div className="flex gap-2 mt-2 overflow-x-auto">
                  {images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImageIdx(idx)}
                      className={`flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition-colors ${
                        idx === selectedImageIdx
                          ? 'border-blue-500 dark:border-blue-400'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                      }`}
                    >
                      <img src={img.file_path} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="w-full aspect-[4/3] bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
            <svg className="w-16 h-16 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
            </svg>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && hasImages && (
        <div
          className={`fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 ${MODAL_OVERLAY}`}
          onClick={() => setLightboxOpen(false)}
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 text-white text-3xl font-bold hover:text-gray-300 z-50"
            aria-label="Close lightbox"
          >
            &times;
          </button>
          <img
            src={images[selectedImageIdx]?.file_path}
            alt={listing.title}
            className="max-w-4xl max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Title & Category */}
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{listing.title}</h1>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {listing.category_name && (
          <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
            {listing.category_name}
          </span>
        )}
        {listing.status && listing.status !== 'active' && (
          <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${LISTING_STATUS_COLORS[listing.status] || ''}`}>
            {listing.status.charAt(0).toUpperCase() + listing.status.slice(1)}
          </span>
        )}
      </div>

      {/* Location */}
      {listing.location && (
        <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 mb-4">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
          </svg>
          <span>{listing.location}</span>
        </div>
      )}

      {/* Price Section */}
      <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4 mb-6">
        {listing.is_poa ? (
          <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
            Price on Application
          </span>
        ) : (
          <div className="space-y-1">
            {/* 🚨 `!= null` AND NOT `> 0` — a £0 rate is a PRICE (owner,
                2026-09-20): a free or loaned item is a real offer, and POA is
                already the separate "ask me" state. The `> 0` gates printed
                "No price specified" over a rate the server had stored
                correctly, while `MarketplaceBrowse.js` rendered the same
                listing as £0.00/day. 🪤 `!= null` is deliberately loose — it
                catches `undefined` too, and it is indifferent to whether the
                API hands back the number `0` or the string `'0'`, which a
                truthiness test is not. Pinned by `MarketplaceZeroRate.test.js`. */}
            {listing.daily_rate != null && (
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {formatMoney(listing.daily_rate)} <span className="text-sm font-normal text-gray-500 dark:text-gray-400">/ day</span>
              </p>
            )}
            {listing.weekly_rate != null && (
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {formatMoney(listing.weekly_rate)} <span className="text-sm font-normal text-gray-500 dark:text-gray-400">/ week</span>
              </p>
            )}
            {/* 🔑 NARROWED, not deleted. A non-POA listing with both rates
                genuinely absent still has to say so — "no price" now means
                `null`, and only `null`. */}
            {listing.daily_rate == null && listing.weekly_rate == null && (
              <p className="text-sm text-gray-500 dark:text-gray-400">No price specified</p>
            )}
          </div>
        )}
      </div>

      {/* Description */}
      {listing.description && (
        <div className="mb-6">
          <p className="whitespace-pre-line text-gray-700 dark:text-gray-300">{listing.description}</p>
        </div>
      )}

      {/* Notes */}
      {listing.notes && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Additional Notes</h2>
          <p className="whitespace-pre-line text-gray-700 dark:text-gray-300 text-sm">{listing.notes}</p>
        </div>
      )}

      {/* Availability */}
      {listing.availability && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Availability</h2>
          <p className="whitespace-pre-line text-gray-700 dark:text-gray-300 text-sm">{listing.availability}</p>
        </div>
      )}

      {/* Enquire section (non-owner) */}
      {!listing.is_owner && (
        <div className="mb-6">
          <button
            className={`${BTN_LG.primary} w-full justify-center`}
            onClick={() => setShowContact(prev => !prev)}
          >
            {showContact ? 'Hide Contact Info' : 'Enquire'}
          </button>
          {showContact && (() => {
            // 🔒 LISTING CONTACTS ONLY — owner ruling 2026-09-08 (M4, CLOSED same
            // day). These two read `listing.contact_email || listing.poster_email`
            // (and phone likewise) until then, so a poster who deliberately
            // CLEARED the listing's contact fields still had their ACCOUNT email
            // and phone shown to every authenticated stranger. The server half no
            // longer SELECTS `poster_email`/`poster_phone` at all, for anyone,
            // owner included (`server/routes/marketplace.js`, GET /listings/:id),
            // so the old fallback would be reaching for keys that never exist on
            // the wire — but it is removed rather than left as harmless dead code,
            // because the whole point of the ruling is that this panel shows what
            // the POSTER TYPED and nothing else. This block only renders for a
            // non-owner (`!listing.is_owner` above), so there is no owner branch
            // to keep.
            // 🕶️ `poster_name` STAYS as the name fallback and that is deliberate:
            // a display name is already on every browse row (GET /listings
            // selects it), so withholding it discloses nothing and only leaves
            // the panel unheaded. The ruling names email and phone.
            const name = listing.contact_name || listing.poster_name;
            const email = listing.contact_email;
            const phone = listing.contact_phone;
            return (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-3">
              {name && (
                <p className="font-semibold text-gray-900 dark:text-white mb-2">{name}</p>
              )}
              {email && (
                <p className="text-sm mb-1">
                  <a href={`mailto:${email}`} className="text-blue-600 dark:text-blue-400 underline">
                    {email}
                  </a>
                </p>
              )}
              {phone && (
                <p className="text-sm mb-1">
                  <a href={`tel:${phone}`} className="text-blue-600 dark:text-blue-400 underline">
                    {phone}
                  </a>
                </p>
              )}
              {!email && !phone && (
                <p className="text-sm text-gray-500 dark:text-gray-400">No contact details provided on this listing.</p>
              )}
            </div>
          );
          })()}
        </div>
      )}

      {/* Owner actions */}
      {!!listing.is_owner && (
        <div className="flex flex-wrap gap-3 mb-6">
          <button
            className={BTN.primary}
            onClick={() => navigate(`/hireforge/listings/${id}/edit`)}
          >
            Edit Listing
          </button>
          <button
            className={listing.status === 'paused' ? BTN.confirm : BTN.warning}
            onClick={handleStatusToggle}
            disabled={statusLoading}
          >
            {statusLoading
              ? 'Updating...'
              : listing.status === 'paused'
                ? 'Unpause Listing'
                : 'Pause Listing'}
          </button>
        </div>
      )}
    </div>
  );
}

export default MarketplaceListingDetail;
